import type { OfficialStatusLink as OfficialStatusLinkData } from "@/lib/conditions/ski/official-status-links";
import { ScoreConfidenceBadge } from "@/app/_components/conditions/ScoreConfidenceBadge";
import { OfficialStatusLink } from "./OfficialStatusLink";
import type { SkiExtentSummary, SkiV3View } from "./ski-v3-view";

/**
 * The limited-terrain line under the piste headline, with an info disclosure. Rendered only
 * when the extent factor is below 1.
 */
export function SkiExtentNote({ summary }: { summary: SkiExtentSummary }) {
  return (
    <div className="rounded-[12px] border border-amber-200 bg-amber-50 px-3.5 py-2.5" data-ski-extent="">
      <p className="text-[13px] font-semibold leading-[1.45] text-amber-900">{summary.line}</p>
      <details className="group mt-0.5">
        <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1.5 text-[12px] font-semibold text-amber-900 underline decoration-amber-400 underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
          <span aria-hidden="true">ⓘ</span>
          Why limited terrain lowers the score
        </summary>
        <p className="pb-1 text-[12.5px] leading-[1.55] text-amber-900">{summary.detail}</p>
      </details>
    </div>
  );
}

/**
 * v3 detail under a ski headline: the extent line (piste), the confidence class with its
 * likely range and flags, and the official status link. Returns null when there is nothing
 * to add, so revision 2 rows without a known status page keep today's UI.
 */
export function SkiHeadlineDetails({
  view,
  extent = null,
  officialLink = null,
}: {
  view: SkiV3View | null;
  extent?: SkiExtentSummary | null;
  officialLink?: OfficialStatusLinkData | null;
}) {
  const showBadge = view?.cls != null;
  if (!showBadge && !extent && !officialLink) return null;
  return (
    <div className="mt-3 space-y-2" data-ski-headline-details="">
      {extent && <SkiExtentNote summary={extent} />}
      {showBadge && view && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.8px] text-text-muted">How sure we are</span>
          <ScoreConfidenceBadge confidence="full" cls={view.cls} lo={view.lo} hi={view.hi} flags={view.flags} />
        </div>
      )}
      {officialLink && <OfficialStatusLink link={officialLink} />}
    </div>
  );
}
